import { useEffect, useState } from 'react';
import type { OutletPayMethods, PayMethod, PayMethods } from '@kassa/shared';
import { api, ApiError } from '../../api';

// Ödəniş növləri (KT-95): sahib obyekt üzrə hansı növlərin kassada olacağını seçir. Kassada görünən = obyektdə açıq
// VƏ kassirin rolunda icazəli («Rollar»). Ən azı bir növ açıq qalır. Bonus və nisyə öz modulları ilə.

const METHODS: { key: PayMethod; label: string }[] = [
  { key: 'cash', label: 'Nağd' },
  { key: 'card', label: 'Kart' },
  { key: 'transfer', label: 'Köçürmə' },
  { key: 'bonus', label: 'Bonus' },
  { key: 'credit', label: 'Nisyə' },
];

export function PayMethodsPanel() {
  const [rows, setRows] = useState<OutletPayMethods[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .payMethodsSettings()
      .then(setRows)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Ödəniş növləri yüklənmədi'));

  useEffect(() => {
    void load();
  }, []);

  const set = (outletId: string, key: PayMethod, value: boolean) =>
    setRows((cur) => cur?.map((r) => (r.outletId === outletId ? { ...r, methods: { ...r.methods, [key]: value } } : r)) ?? null);

  async function save(targets: OutletPayMethods[], methods?: PayMethods) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      for (const r of targets) await api.payMethodsSave(r.outletId, methods ?? r.methods);
      await load();
      setNotice(targets.length > 1 ? 'Bütün obyektlərə tətbiq olundu.' : `«${targets[0].outletName}» saxlanıldı.`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Saxlanmadı');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-label="Ödəniş növləri">
      <h2>Ödəniş növləri</h2>
      <p className="muted small">Kassada yalnız açıq növlər görünür. Kassirə hansı növlərin icazəli olduğu «Rollar»-da seçilir.</p>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="ok" role="status">
          {notice}
        </p>
      )}
      {!rows && !error && <p className="muted">Yüklənir…</p>}
      {rows?.map((r) => (
        <fieldset key={r.outletId} className="perm-group" disabled={busy}>
          <legend>{r.outletName}</legend>
          {METHODS.map((m) => {
            const moduleOff = (m.key === 'bonus' && !r.modules.bonus) || (m.key === 'credit' && !r.modules.credit);
            return (
              <label key={m.key} className="check-row">
                <input type="checkbox" checked={r.methods[m.key] && !moduleOff} disabled={moduleOff} onChange={(e) => set(r.outletId, m.key, e.target.checked)} />
                <span>
                  {m.label}
                  {moduleOff && <span className="muted small"> — paketdə yoxdur</span>}
                </span>
              </label>
            );
          })}
          <span className="toolbar">
            <button type="button" className="btn-small" onClick={() => save([r])}>
              Saxla
            </button>
            {rows.length > 1 && (
              <button type="button" className="link-btn" onClick={() => save(rows, r.methods)}>
                Bütün obyektlərə tətbiq et
              </button>
            )}
          </span>
        </fieldset>
      ))}
    </section>
  );
}
